import { useContext, useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";

import type { MhaStatus } from "@thalamos/aspire-common";
import {
  canViewPatientList,
  defaultPatientListSort,
  isPatientListEnabled,
} from "@thalamos/aspire-common";

import { PatientList } from "./PatientList.js";
import type { PatientListSort } from "./SortableColumnHeader.js";
import { api, apiHooks } from "~/api.js";
import {
  Banner,
  BannerList,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";

const ROWS_PER_PAGE = 10;

const SEARCH_DEBOUNCE_MS = 300;

async function removedFromList(rootPatientId: string): Promise<boolean> {
  try {
    const response = await api.patientList.remove(rootPatientId);
    return response.status >= 200 && response.status < 300;
  } catch {
    return false;
  }
}

export function PatientListPage(_props: Record<string, never>) {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useContext(LoggedInUserContext)?.user;

  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<PatientListSort>(defaultPatientListSort);
  const [searchTerm, setSearchTerm] = useState("");
  const [mhaStatuses, setMhaStatuses] = useState<MhaStatus[]>([]);
  const [locationNames, setLocationNames] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState("");

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed === searchFilter) return;
    const timer = setTimeout(() => {
      setSearchFilter(trimmed);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, searchFilter]);

  const canView = canViewPatientList(user);

  const [{ data, loading, error }, refetch] = apiHooks.patientList.list(
    page * ROWS_PER_PAGE,
    ROWS_PER_PAGE,
    sort.sortBy,
    sort.sortDirection,
    searchFilter === "" ? undefined : searchFilter,
    mhaStatuses,
    locationNames,
    !canView,
  );

  const removesPatient = async (rootPatientId: string) => {
    if (!(await removedFromList(rootPatientId))) {
      renderErrorToast({
        message: "The patient could not be removed from your patient list",
      });
      return;
    }

    renderSuccessToast({ message: "Patient removed from your patient list" });

    const emptiesThisPage = page > 0 && data?.entries.length === 1;
    if (emptiesThisPage) {
      setPage(page - 1);
    } else {
      // A reload that throws shows through the hook's own error state.
      void refetch().catch(() => undefined);
    }
  };

  // Hiding a menu entry is not access control; the URL must be checked too. Kept
  // as two refusals so the message names the actual reason.
  if (!isPatientListEnabled(user)) {
    return (
      <Banner
        bannerType={BannerList.ERROR}
        title="The patient list is not enabled for your organisation"
      />
    );
  }

  if (!canView) {
    return (
      <Banner
        bannerType={BannerList.ERROR}
        title="The patient list is only available while you are working in a team"
      />
    );
  }

  return (
    <Box sx={{ padding: { xs: "1em 0.5em", sm: "1em 1.5em" } }}>
      <Box
        sx={{
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          paddingLeft: "0.8em",
          marginBottom: "1em",
        }}
      >
        <Typography component="h1" sx={{ fontSize: "1.3rem", fontWeight: 700 }}>
          Patient list
        </Typography>
      </Box>

      <PatientList
        entries={data?.entries ?? []}
        total={data?.total ?? 0}
        page={page}
        rowsPerPage={ROWS_PER_PAGE}
        isLoading={loading}
        error={error ? "The patient list could not be loaded." : undefined}
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(0);
        }}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onClearFilters={() => {
          setSort(defaultPatientListSort);
          setMhaStatuses([]);
          setLocationNames([]);
          setPage(0);
        }}
        availableMhaStatuses={data?.availableMhaStatuses ?? []}
        selectedMhaStatuses={mhaStatuses}
        onMhaStatusesChange={(statuses) => {
          setMhaStatuses(statuses);
          setPage(0);
        }}
        availableLocationNames={data?.availableLocationNames ?? []}
        selectedLocationNames={locationNames}
        onLocationNamesChange={(names) => {
          setLocationNames(names);
          setPage(0);
        }}
        onPageChange={setPage}
        onAddPatient={() => {
          navigate(routeFns.patientSearch(routeFns.patientList()));
        }}
        onRemovePatient={removesPatient}
      />
    </Box>
  );
}
